import React, { useEffect, useState } from 'react';
import { supportAPI } from '@/services/api';
import { LifeBuoy, Check, Ban, RefreshCw, Loader2, KeyRound, Copy, Trash2, Plus } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const SUPPORT_DOMAIN = 'numuv.app';

function pretty(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

function formatRemaining(expiresAt) {
  if (!expiresAt) return '—';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export default function AdminSupportAgents() {
  const [tab, setTab] = useState('pending');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const pAgents = usePagination(agents, 20);

  // Invite codes
  const [inviteTab, setInviteTab] = useState('active');
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  // Create-invite form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ localpart: '', duration_value: 60, duration_unit: 'minutes' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newlyCreatedCode, setNewlyCreatedCode] = useState(null);

  const loadAgents = async () => {
    setLoading(true);
    try { const d = await supportAPI.adminListAgents({ status: tab }); setAgents(d.agents || []); }
    catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { loadAgents(); }, [tab]);

  const loadInvites = async () => {
    setInvitesLoading(true);
    try { const d = await supportAPI.adminListInvites({ status: inviteTab }); setInvites(d.invites || []); }
    catch (e) { console.error(e); }
    setInvitesLoading(false);
  };
  useEffect(() => { loadInvites(); }, [inviteTab]);

  // Auto-refresh remaining-time every 30s on the active tab
  useEffect(() => {
    if (inviteTab !== 'active') return;
    const t = setInterval(() => setInvites(prev => [...prev]), 30000);
    return () => clearInterval(t);
  }, [inviteTab]);

  const act = async (action, id) => {
    try {
      if (action === 'approve') await supportAPI.adminApproveAgent(id);
      if (action === 'revoke') await supportAPI.adminRevokeAgent(id);
      if (action === 'restore') await supportAPI.adminRestoreAgent(id);
      await loadAgents();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    setNewlyCreatedCode(null);
    try {
      const localpart = (createForm.localpart || '').trim().toLowerCase().split('@')[0];
      if (!localpart) { setCreateError('Username (before @) is required'); setCreating(false); return; }
      const dv = parseInt(createForm.duration_value, 10);
      if (!dv || dv <= 0) { setCreateError('Duration must be a positive number'); setCreating(false); return; }
      const r = await supportAPI.adminCreateInvite({
        email_localpart: localpart,
        duration_value: dv,
        duration_unit: createForm.duration_unit,
      });
      setNewlyCreatedCode(r.invite);
      setCreateForm({ localpart: '', duration_value: 60, duration_unit: 'minutes' });
      await loadInvites();
    } catch (err) {
      setCreateError(err?.response?.data?.detail || 'Failed to create invite');
    } finally { setCreating(false); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite code? It will become unusable.')) return;
    try { await supportAPI.adminRevokeInvite(id); await loadInvites(); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed to revoke'); }
  };

  const copyCode = (code) => {
    try { navigator.clipboard?.writeText(code); } catch { /* noop */ }
  };

  return (
    <div data-testid="admin-support-agents">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><LifeBuoy className="w-5 h-5" /> Support Agents</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Generate invite codes for new agents, then approve or revoke them.</p>

      {/* ===================== Invite codes panel ===================== */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl p-5 mb-8" data-testid="invite-codes-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2"><KeyRound className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Invite codes</h2>
          <button onClick={() => { setShowCreate(s => !s); setNewlyCreatedCode(null); setCreateError(''); }}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20"
            data-testid="toggle-create-invite">
            <Plus className="w-3 h-3" /> {showCreate ? 'Cancel' : 'Generate new code'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={createInvite} className="space-y-3 mb-5 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-4" data-testid="create-invite-form">
            {createError && <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-3 py-2 rounded text-xs" data-testid="create-invite-error">{createError}</div>}
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">Support agent email</label>
              <div className="flex items-stretch bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-md overflow-hidden">
                <input
                  type="text"
                  value={createForm.localpart}
                  onChange={e => setCreateForm({ ...createForm, localpart: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                  required
                  placeholder="username"
                  data-testid="invite-localpart"
                  className="flex-1 bg-transparent px-3 py-2 text-slate-900 dark:text-white text-sm focus:outline-none"
                />
                <div className="flex items-center px-3 text-slate-500 dark:text-zinc-500 text-sm border-l border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-[#0a0a0b] select-none">@{SUPPORT_DOMAIN}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">Valid for</label>
                <input
                  type="number"
                  min="1"
                  value={createForm.duration_value}
                  onChange={e => setCreateForm({ ...createForm, duration_value: e.target.value })}
                  required
                  data-testid="invite-duration-value"
                  className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-md px-3 py-2 text-slate-900 dark:text-white text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">Unit</label>
                <select
                  value={createForm.duration_unit}
                  onChange={e => setCreateForm({ ...createForm, duration_unit: e.target.value })}
                  data-testid="invite-duration-unit"
                  className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-md px-3 py-2 text-slate-900 dark:text-white text-sm focus:outline-none"
                >
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={creating}
              className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="submit-create-invite">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
              Generate code
            </button>
          </form>
        )}

        {newlyCreatedCode && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-5" data-testid="newly-created-invite">
          <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-2">Share this code in person with <strong>{newlyCreatedCode.target_email}</strong>. It is single-use and expires {pretty(newlyCreatedCode.expires_at)}.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-slate-50 dark:bg-[#0a0a0b] border border-emerald-500/30 rounded font-mono text-lg text-emerald-700 dark:text-emerald-300 tracking-widest text-center" data-testid="invite-code-value">
                {newlyCreatedCode.code}
              </code>
              <button onClick={() => copyCode(newlyCreatedCode.code)} className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded text-xs inline-flex items-center gap-1 hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="copy-invite-code">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-3">
          {['active', 'used', 'expired', 'revoked', 'all'].map(t => (
            <button key={t} onClick={() => setInviteTab(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${inviteTab === t ? 'bg-slate-900 dark:bg-white text-white dark:text-black' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'}`}
              data-testid={`invite-tab-${t}`}>{t}</button>
          ))}
        </div>
        <ScrollHint>
        <div className="overflow-x-auto border border-slate-200 dark:border-zinc-800/60 rounded-lg">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Target email</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Expires in / on</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {invitesLoading && <tr><td colSpan={5} className="p-6 text-center text-slate-500 dark:text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
              {!invitesLoading && invites.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500 dark:text-zinc-500">No invites in this state.</td></tr>}
              {invites.map(inv => (
                <tr key={inv.id} className="border-t border-slate-200 dark:border-zinc-800/50" data-testid={`invite-row-${inv.id}`}>
                  <td className="p-3"><code className="text-emerald-700 dark:text-emerald-300 font-mono text-xs">{inv.code}</code></td>
                  <td className="p-3 text-slate-600 dark:text-zinc-300 text-xs">{inv.target_email}</td>
                  <td className="p-3 text-xs">
                    {inv.used ? <span className="text-slate-500 dark:text-zinc-400">Used</span>
                      : inv.is_expired ? <span className="text-amber-700 dark:text-amber-300">Expired</span>
                        : inv.is_active === false ? <span className="text-red-700 dark:text-red-300">Revoked</span>
                          : <span className="text-emerald-700 dark:text-emerald-300">Active</span>}
                  </td>
                  <td className="p-3 text-slate-500 dark:text-zinc-500 text-xs">
                    {inv.is_active ? formatRemaining(inv.expires_at) : pretty(inv.expires_at)}
                  </td>
                  <td className="p-3 text-right">
                    {inv.is_active && (
                      <button onClick={() => revokeInvite(inv.id)} className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs inline-flex items-center gap-1" data-testid={`revoke-invite-${inv.id}`}>
                        <Trash2 className="w-3 h-3" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </ScrollHint>
      </div>

      {/* ===================== Agents panel ===================== */}
      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'revoked', 'all'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${tab === t ? 'bg-slate-900 dark:bg-white text-white dark:text-black' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>
      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Status</th><th className="text-left p-3">Joined</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-slate-500 dark:text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
            {!loading && agents.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500 dark:text-zinc-500">No agents in this state.</td></tr>}
            {agents.length === 0 ? null : pAgents.pageItems.map(a => (
              <tr key={a.id} className="border-t border-slate-200 dark:border-zinc-800/50" data-testid={`agent-row-${a.id}`}>
                <td className="p-3 text-slate-900 dark:text-white">{a.name || '—'}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{a.email}</td>
                <td className="p-3 text-xs">
                  {a.is_disabled ? <span className="text-red-700 dark:text-red-300">Revoked</span>
                    : a.approved ? <span className="text-emerald-700 dark:text-emerald-300">Approved</span>
                      : <span className="text-amber-700 dark:text-amber-300">Pending</span>}
                </td>
                <td className="p-3 text-slate-500 dark:text-zinc-500 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</td>
                <td className="p-3 text-right space-x-3 whitespace-nowrap">
                  {!a.approved && !a.is_disabled && <button onClick={() => act('approve', a.id)} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`approve-${a.id}`}><Check className="w-3 h-3" /> Approve</button>}
                  {a.approved && !a.is_disabled && <button onClick={() => act('revoke', a.id)} className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs inline-flex items-center gap-1" data-testid={`revoke-${a.id}`}><Ban className="w-3 h-3" /> Revoke</button>}
                  {a.is_disabled && <button onClick={() => act('restore', a.id)} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`restore-${a.id}`}><RefreshCw className="w-3 h-3" /> Restore</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ScrollHint>
      <Pagination {...pAgents.paginationProps} testIdPrefix="admin-agents-pg" />
    </div>
  );
}
